import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Clock, CheckCircle, ExternalLink, FileText, Image, Link2, Play, Square, Award, StickyNote, Save, ChevronRight, ChevronLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LabResearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeViewer, setActiveViewer] = useState(null);
  const [trackingTime, setTrackingTime] = useState({});
  const timerRefs = useRef({});
  const trackIntervalRef = useRef(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteLastSaved, setNoteLastSaved] = useState(null);
  const noteSaveTimer = useRef(null);

  // Get classroom_id from student data
  const [classroomId, setClassroomId] = useState(null);

  useEffect(() => {
    const loadStudentData = async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}/api/student/me`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          const students = Array.isArray(data) ? data : (data.students || []);
          if (students.length > 0) {
            setClassroomId(students[0].classroom_id);
          }
        }
      } catch (e) { console.error('Load student data error:', e); }
    };
    loadStudentData();
  }, []);

  useEffect(() => {
    if (classroomId) loadMaterials();
  }, [classroomId]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timerRefs.current).forEach(t => clearInterval(t));
      if (trackIntervalRef.current) clearInterval(trackIntervalRef.current);
      if (noteSaveTimer.current) clearTimeout(noteSaveTimer.current);
    };
  }, []);

  const loadMaterials = async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/materials/${classroomId}`, { credentials: 'include' });
      if (resp.ok) setMaterials(await resp.json());
    } catch (e) {
      toast.error('Erro ao carregar materiais');
    } finally {
      setLoading(false);
    }
  };

  const startTimer = (materialId) => {
    if (timerRefs.current[materialId]) return;
    setTrackingTime(prev => ({ ...prev, [materialId]: prev[materialId] || 0 }));
    timerRefs.current[materialId] = setInterval(() => {
      setTrackingTime(prev => ({ ...prev, [materialId]: (prev[materialId] || 0) + 1 }));
    }, 1000);
    // Send time to server every 15 seconds
    trackIntervalRef.current = setInterval(() => {
      sendTrackTime(materialId, 15);
    }, 15000);
  };

  const stopTimer = (materialId) => {
    if (timerRefs.current[materialId]) {
      clearInterval(timerRefs.current[materialId]);
      delete timerRefs.current[materialId];
    }
    if (trackIntervalRef.current) {
      clearInterval(trackIntervalRef.current);
      trackIntervalRef.current = null;
    }
    // Send remaining time
    const elapsed = trackingTime[materialId] || 0;
    const remainder = elapsed % 15;
    if (remainder > 0) sendTrackTime(materialId, remainder);
  };

  const sendTrackTime = async (materialId, seconds) => {
    try {
      await fetch(`${BACKEND_URL}/api/lab/track-time`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ material_id: materialId, seconds })
      });
    } catch (e) { console.error('Track time error:', e); }
  };

  const openMaterial = (mat) => {
    setNoteContent('');
    setNoteLastSaved(null);
    setNotesOpen(false);
    if (mat.material_type === 'link') {
      window.open(mat.link_url, '_blank');
      startTimer(mat.material_id);
      setActiveViewer(mat.material_id);
    } else {
      setActiveViewer(mat.material_id);
      startTimer(mat.material_id);
    }
    loadNotes(mat.material_id);
  };

  const closeMaterial = () => {
    if (activeViewer) {
      stopTimer(activeViewer);
      // Flush pending note save
      if (noteSaveTimer.current) {
        clearTimeout(noteSaveTimer.current);
        noteSaveTimer.current = null;
      }
      if (noteContent) saveNotes(activeViewer, noteContent);
      setActiveViewer(null);
      setNotesOpen(false);
      setNoteContent('');
      loadMaterials();
    }
  };

  const completeReading = async (materialId) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/complete-reading`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ material_id: materialId })
      });
      if (resp.ok) {
        const data = await resp.json();
        toast.success(`Leitura registrada! +${data.xp_earned} XP`);
        loadMaterials();
      } else {
        const err = await resp.json();
        toast.error(err.detail || 'Erro');
      }
    } catch (e) {
      toast.error('Erro ao registrar leitura');
    }
  };

  const loadNotes = async (materialId) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/notes/${materialId}`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setNoteContent(data.content || '');
        setNoteLastSaved(data.updated_at);
      }
    } catch (e) { console.error('Load notes error:', e); }
  };

  const saveNotes = async (materialId, content) => {
    setNoteSaving(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/notes/${materialId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      });
      if (resp.ok) {
        const data = await resp.json();
        setNoteLastSaved(data.updated_at);
      }
    } catch (e) { console.error('Save notes error:', e); }
    finally { setNoteSaving(false); }
  };

  const handleNoteChange = (materialId, value) => {
    setNoteContent(value);
    if (noteSaveTimer.current) clearTimeout(noteSaveTimer.current);
    noteSaveTimer.current = setTimeout(() => saveNotes(materialId, value), 1500);
  };

  const formatTime = (s) => {
    if (!s || s <= 0) return '0s';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700 uppercase tracking-wider">Concluído</span>;
      case 'in_progress':
        return <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-700 uppercase tracking-wider">Em Estudo</span>;
      default:
        return <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-slate-100 text-slate-500 uppercase tracking-wider">Pendente</span>;
    }
  };

  const getFileIcon = (mat) => {
    if (mat.material_type === 'link') return <Link2 className="w-5 h-5 text-blue-500" />;
    if (mat.file_type === 'pdf') return <FileText className="w-5 h-5 text-red-500" />;
    if (['png', 'jpg', 'jpeg', 'webp'].includes(mat.file_type)) return <Image className="w-5 h-5 text-purple-500" />;
    return <FileText className="w-5 h-5 text-slate-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  // Viewer modal
  if (activeViewer) {
    const mat = materials.find(m => m.material_id === activeViewer);
    if (!mat) return null;
    const elapsed = trackingTime[activeViewer] || 0;
    const canComplete = elapsed >= 120 && mat.reading_status !== 'completed';
    const isLink = mat.material_type === 'link';
    const fileUrl = `${BACKEND_URL}/api/lab/file/${mat.material_id}`;

    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button onClick={closeMaterial} variant="ghost" size="sm" data-testid="close-viewer-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
              </Button>
              <h2 className="text-sm font-outfit font-bold text-slate-800 truncate max-w-md">{mat.title}</h2>
            </div>
            <div className="flex items-center gap-3">
              {/* Notes toggle */}
              <Button
                data-testid="toggle-notes-btn"
                onClick={() => setNotesOpen(prev => !prev)}
                variant={notesOpen ? 'default' : 'outline'}
                size="sm"
                className={`rounded-xl text-xs font-bold ${notesOpen ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'border-indigo-200 text-indigo-600 hover:bg-indigo-50'}`}
              >
                <StickyNote className="w-3.5 h-3.5 mr-1" />
                Anotações
                {noteContent && !notesOpen && <span className="ml-1 w-2 h-2 rounded-full bg-indigo-400 inline-block" />}
              </Button>
              {/* Timer */}
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold font-nunito ${elapsed >= 120 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="study-timer">
                <Clock className="w-3.5 h-3.5" />
                {formatTime(elapsed)}
              </div>
              {/* Complete button */}
              {mat.reading_status !== 'completed' && (
                <Button
                  data-testid="complete-reading-btn"
                  onClick={() => completeReading(mat.material_id)}
                  disabled={!canComplete}
                  size="sm"
                  className={`font-bold ${canComplete ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                >
                  <Award className="w-3.5 h-3.5 mr-1" />
                  {canComplete ? 'Registrar Leitura (+10 AURA)' : `Leia por mais ${formatTime(Math.max(0, 120 - elapsed))}`}
                </Button>
              )}
              {mat.reading_status === 'completed' && (
                <span className="flex items-center gap-1 text-sm text-emerald-600 font-bold"><CheckCircle className="w-4 h-4" /> Concluído</span>
              )}
            </div>
          </div>
        </nav>
        <div className="flex h-[calc(100vh-57px)]">
          {/* Content area */}
          <div className={`flex-1 transition-all duration-300 ${notesOpen ? 'mr-0' : ''}`}>
            {isLink ? (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                <ExternalLink className="w-20 h-20 text-slate-300" />
                <p className="text-slate-600 font-nunito">O link abriu em uma nova aba.</p>
                <p className="text-sm text-slate-400 font-nunito">O timer continua rodando enquanto você estuda.</p>
              </div>
            ) : mat.file_type === 'pdf' ? (
              <iframe src={fileUrl} className="w-full h-full border-0" title={mat.title} />
            ) : ['png', 'jpg', 'jpeg', 'webp'].includes(mat.file_type) ? (
              <div className="flex items-center justify-center h-full p-8 bg-slate-50">
                <img src={fileUrl} alt={mat.title} className="max-w-full max-h-full object-contain rounded-lg shadow-lg" />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-500 font-nunito">Visualização não disponível. <a href={fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Baixar arquivo</a></p>
              </div>
            )}
          </div>
          {/* Notes panel */}
          {notesOpen && (
            <div className="w-80 border-l border-slate-200 bg-white flex flex-col shadow-lg" data-testid="notes-panel">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-violet-50">
                <div className="flex items-center gap-2">
                  <StickyNote className="w-4 h-4 text-indigo-600" />
                  <span className="font-outfit font-bold text-sm text-slate-800">Minhas Anotações</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {noteSaving && <span className="text-[10px] text-indigo-400 font-nunito animate-pulse">Salvando...</span>}
                  {!noteSaving && noteLastSaved && <span className="text-[10px] text-emerald-500 font-nunito">Salvo</span>}
                </div>
              </div>
              <textarea
                data-testid="notes-textarea"
                className="flex-1 p-4 text-sm text-slate-700 font-nunito resize-none focus:outline-none placeholder:text-slate-300 leading-relaxed"
                placeholder="Escreva suas anotações sobre este material aqui...&#10;&#10;Dicas: marque pontos importantes, dúvidas e insights."
                value={noteContent}
                onChange={(e) => handleNoteChange(mat.material_id, e.target.value)}
              />
              <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[10px] text-slate-400 font-nunito">{noteContent.length} caracteres</span>
                <Button
                  data-testid="save-notes-btn"
                  size="sm"
                  variant="ghost"
                  className="text-xs text-indigo-600 hover:bg-indigo-50"
                  onClick={() => { if (noteSaveTimer.current) clearTimeout(noteSaveTimer.current); saveNotes(mat.material_id, noteContent); }}
                >
                  <Save className="w-3 h-3 mr-1" /> Salvar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/student/dashboard')} variant="ghost" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2" data-testid="lab-title">
            Laboratório de Pesquisa
          </h1>
          <p className="text-slate-600 font-nunito">Explore os materiais do professor e ganhe AURA por estudar</p>
        </div>

        {materials.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-16 text-center">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-nunito text-lg">Nenhum material disponível ainda</p>
            <p className="text-sm text-slate-400 font-nunito mt-1">O professor ainda não enviou materiais de pesquisa</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {materials.map((mat) => (
              <div
                key={mat.material_id}
                data-testid="lab-material-card"
                className={`bg-white rounded-2xl border-2 p-5 transition-all duration-200 hover:shadow-md cursor-pointer ${
                  mat.reading_status === 'completed' ? 'border-emerald-200 bg-emerald-50/30' :
                  mat.reading_status === 'in_progress' ? 'border-amber-200' : 'border-slate-100'
                }`}
                onClick={() => openMaterial(mat)}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
                    {getFileIcon(mat)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-outfit font-bold text-sm text-slate-900 truncate">{mat.title}</h3>
                    <p className="text-xs text-slate-400 font-nunito mt-0.5">{mat.area}</p>
                  </div>
                  {getStatusBadge(mat.reading_status)}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-xs text-slate-400 font-nunito">
                    <Clock className="w-3 h-3" />
                    <span>{mat.time_spent > 0 ? formatTime(mat.time_spent) : 'Não iniciado'}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs font-bold text-blue-600">
                    <Play className="w-3 h-3" />
                    <span>{mat.reading_status === 'completed' ? 'Revisar' : 'Estudar'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LabResearch;
